/**
 * Structural Number (SN) calculation service for AASHTO pavement design
 * Based on exact Excel formulas from Design_LCR.txt
 *
 * SN = Σ(ai * Di * mi) where:
 * - ai = layer coefficient for layer i
 * - Di = thickness of layer i (inches)
 * - mi = drainage coefficient for layer i
 */

import { IPavedData, IAsphaltData } from '../api/IApiResponse';
import { materialDatabaseService } from './MaterialDatabaseService';

export interface LayerData {
    materialId?: string;
    thickness?: number; // in meters
    layerCoefficient?: number;
    drainageCoefficient?: number;
}

export interface PavementLayers {
    surface?: LayerData;
    base?: LayerData;
    subbase?: LayerData;
}

/**
 * Service for calculating Structural Number (SN) for AASHTO pavement design
 */
export class StructuralNumberService {
    private static readonly METERS_TO_INCHES = 39.3701; // Conversion factor from meters to inches
    private materialService = materialDatabaseService;

    /**
     * Static method to calculate Structural Number from project data
     * @param projectData - Complete project data including layer information
     * @returns Structural Number in inches
     */
    public static calculateFromProjectData(projectData: any): number {
        const service = new StructuralNumberService();

        // Extract layer data from project data
        const layers: PavementLayers = {
            surface: {
                materialId: projectData.surface?.surfaceMaterial,
                thickness: projectData.surface?.minThickness ? projectData.surface.minThickness / 1000 : undefined, // Convert mm to meters
                layerCoefficient: projectData.surface?.layerCoefficienta1,
            },
            base: {
                materialId: projectData.baseCourse?.baseMaterial,
                thickness: projectData.baseCourse?.minThickness ? projectData.baseCourse.minThickness / 1000 : undefined, // Convert mm to meters
                layerCoefficient: projectData.baseCourse?.layerCoefficienta2,
            },
            subbase: {
                materialId: projectData.subbaseCourse?.subbaseMaterial,
                thickness: projectData.subbaseCourse?.minThickness ? projectData.subbaseCourse.minThickness / 1000 : undefined, // Convert mm to meters
                layerCoefficient: projectData.subbaseCourse?.layerCoefficienta3,
            },
        };

        return service.calculateStructuralNumber(layers);
    }

    /**
     * Calculate the Structural Number (SN) for given pavement layers
     * @param layers - Pavement layer data (surface, base, subbase)
     * @returns Structural Number in inches
     */
    public calculateStructuralNumber(layers: PavementLayers): number {
        let totalSN = 0;

        // Surface layer (SN1)
        if (layers.surface?.thickness && layers.surface?.materialId) {
            const surfaceData = this.getSurfaceLayerData(layers.surface);
            const sn1 = this.calculateLayerSN(surfaceData.layerCoefficient, surfaceData.thickness, surfaceData.drainageCoefficient);
            totalSN += sn1;
        }

        // Base layer (SN2 - SN1)
        if (layers.base?.thickness && layers.base?.materialId) {
            const baseData = this.getBaseLayerData(layers.base);
            const sn2Increment = this.calculateLayerSN(baseData.layerCoefficient, baseData.thickness, baseData.drainageCoefficient);
            totalSN += sn2Increment;
        }

        // Subbase layer (SN3 - SN2)
        if (layers.subbase?.thickness && layers.subbase?.materialId) {
            const subbaseData = this.getSubbaseLayerData(layers.subbase);
            const sn3Increment = this.calculateLayerSN(subbaseData.layerCoefficient, subbaseData.thickness, subbaseData.drainageCoefficient);
            totalSN += sn3Increment;
        }

        return totalSN;
    }

    /**
     * Calculate SN contribution for a single layer
     * @param layerCoefficient - ai (layer coefficient)
     * @param thicknessMeters - Di (thickness in meters)
     * @param drainageCoefficient - mi (drainage coefficient)
     * @returns SN contribution for this layer
     */
    private calculateLayerSN(layerCoefficient: number, thicknessMeters: number, drainageCoefficient: number): number {
        // Convert thickness from meters to inches
        const thicknessInches = thicknessMeters * StructuralNumberService.METERS_TO_INCHES;

        // AASHTO formula: SN = ai * Di * mi
        return layerCoefficient * thicknessInches * drainageCoefficient;
    }

    /**
     * Get complete surface layer data including coefficients from material database
     */
    private getSurfaceLayerData(layer: LayerData): Required<LayerData> {
        const materialId = layer.materialId!;
        const layerCoefficient = layer.layerCoefficient ?? this.materialService.getSurfaceLayerCoefficient(materialId) ?? 0.44; // Default from Excel

        // Surface layers typically don't have drainage coefficient (m1 = 1.0)
        const drainageCoefficient = layer.drainageCoefficient ?? 1.0;

        return {
            materialId,
            thickness: layer.thickness!,
            layerCoefficient,
            drainageCoefficient,
        };
    }

    /**
     * Get complete base layer data including coefficients from material database
     */
    private getBaseLayerData(layer: LayerData): Required<LayerData> {
        const materialId = layer.materialId!;
        const material = this.materialService.getBaseMaterialById(materialId);

        const layerCoefficient = layer.layerCoefficient ?? material?.layerCoefficient ?? 0.14; // Default from Excel

        const drainageCoefficient = layer.drainageCoefficient ?? material?.drainageCoefficient ?? 1.0; // Default

        return {
            materialId,
            thickness: layer.thickness!,
            layerCoefficient,
            drainageCoefficient,
        };
    }

    /**
     * Get complete subbase layer data including coefficients from material database
     */
    private getSubbaseLayerData(layer: LayerData): Required<LayerData> {
        const materialId = layer.materialId!;
        const material = this.materialService.getSubbaseMaterialById(materialId);

        const layerCoefficient = layer.layerCoefficient ?? material?.layerCoefficient ?? 0.11; // Default from Excel

        const drainageCoefficient = layer.drainageCoefficient ?? material?.drainageCoefficient ?? 1.0; // Default

        return {
            materialId,
            thickness: layer.thickness!,
            layerCoefficient,
            drainageCoefficient,
        };
    }

    /**
     * Extract pavement layer data from project data structure
     * @param projectData - Project data containing layer information
     * @returns Structured pavement layers data
     */
    public extractLayersFromProjectData(projectData: any): PavementLayers {
        const layers: PavementLayers = {};

        // Extract surface layer data
        if (projectData?.surfaceLayer) {
            layers.surface = {
                materialId: projectData.surfaceLayer.surfaceMaterial,
                thickness: projectData.surfaceLayer.thickness,
                layerCoefficient: projectData.surfaceLayer.layerCoefficienta1,
            };
        }

        // Extract base layer data
        if (projectData?.geogridBaseCourse) {
            layers.base = {
                materialId: projectData.geogridBaseCourse.baseMaterial,
                thickness: projectData.geogridBaseCourse.thickness,
                layerCoefficient: projectData.geogridBaseCourse.layerCoefficienta2,
            };
        }

        // Extract subbase layer data
        if (projectData?.geogridSubBaseCourse) {
            layers.subbase = {
                materialId: projectData.geogridSubBaseCourse.subbaseMaterial,
                thickness: projectData.geogridSubBaseCourse.thickness,
                layerCoefficient: projectData.geogridSubBaseCourse.layerCoefficienta3,
            };
        }

        return layers;
    }
}
